// Base Client
import { getApiToken, parseRpcError } from "../helpers";
import { KVP, UpsertUser, User } from "../types";
import { getBffRequest } from "./base/api";

// Protos
import { ClaimsBFFControllerClient } from "./protos/Claims-bffServiceClientPb";
import {
	AuthRequest,
	AuthResponse,
	LoginRequest,
	LoginResponse,
	RefreshTokenRequest,
	RefreshTokenResponse,
	RequestPasswordResetRequest,
	RequestPasswordResetResponse,
	ResetPasswordRequest,
	ResetPasswordResponse,
	RevokeTokenRequest,
	RevokeTokenResponse,
	GetUsersRequest,
	GetUsersResponse,
	CreateUserRequest,
	CreateUserResponse,
	ResendWelcomeRequest,
	ResendWelcomeResponse,
	GetUserByIdRequest,
	GetUserByIdResponse,
	GetUserDataByIdRequest,
	GetUserDataByIdResponse,
	UpsertUserRequest,
	UpsertUserResponse
} from "./protos/messages/auth.message_pb";

/**
 * Handles the AuthService::me endpoint
 * @param token JWT Token string
 * @returns AuthResponse object or void
 */
export const getCurrentUser = async (): Promise<AuthResponse | never> => {
	const token: string = getApiToken();
	const request: AuthRequest = new AuthRequest();
	request.setJwt(token);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.me(request, {})
		.then((response: AuthResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

/**
 * Handles the AuthService::login endpoint
 * @param username User entered Username string
 * @param password User entered Password string
 * @returns LoginResponse object
 */
export const login = async (username: string, password: string): Promise<LoginResponse | never> => {
	const request: LoginRequest = new LoginRequest();
	request.setUsernameOrEmail(username);
	request.setPassword(password);

	const client: ClaimsBFFControllerClient = getBffRequest();
	return client
		.login(request, null)
		.then((response: LoginResponse) => response)
		.catch((err) => {
			console.error(err);
			// throw err;
			throw parseRpcError(err.message).errorMessage;
		});
};

/**
 * Handles the AuthService::login_ad endpoint
 * @param username User entered Username string
 * @param password User entered Password string
 * @returns LoginResponse object
 */
export const login_ad = async (username: string, password: string): Promise<LoginResponse | never> => {
	const request: LoginRequest = new LoginRequest();
	request.setUsernameOrEmail(username);
	request.setPassword(password);

	const client: ClaimsBFFControllerClient = getBffRequest();
	return client
		.loginAD(request, null)
		.then((response: LoginResponse) => {
			return response;
		})
		.catch((err) => {
			console.error(err);
			// throw err;
			throw parseRpcError(err.message).errorMessage;
		});
};

/**
 * Handles the AuthService::login_ad endpoint with Azure token in request body
 * @param azureToken Azure ID token from MSAL
 * @returns LoginResponse object
 */
export const loginWithAzureToken = async (azureToken: string): Promise<LoginResponse | never> => {
	const request: LoginRequest = new LoginRequest();
	request.setUsernameOrEmail("azure ad signin");
	request.setPassword("azure ad signin");
	request.setAzureToken(azureToken);

	const client: ClaimsBFFControllerClient = getBffRequest();

	return client
		.loginAD(request, null)
		.then((response: LoginResponse) => {
			return response;
		})
		.catch((err) => {
			console.error(err);
			throw parseRpcError(err.message).errorMessage;
		});
};

/**
 * Handles the AuthService::refresh_token endpoint
 * @param userId ID of the current User
 * @param token Refresh Token string
 * @returns RefreshTokenResponse object or void
 */
export const refreshToken = async (userId: number, token: string): Promise<RefreshTokenResponse | never> => {
	const request: RefreshTokenRequest = new RefreshTokenRequest();
	request.setToken(token);
	request.setUserId(userId);

	const client: ClaimsBFFControllerClient = getBffRequest();
	return client
		.refreshToken(request, null)
		.then((response: RefreshTokenResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const logout = async (userId: number, refreshToken: string): Promise<RevokeTokenResponse> => {
	const token: string = getApiToken();

	const request: RevokeTokenRequest = new RevokeTokenRequest();
	request.setToken(refreshToken);
	request.setUserId(userId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.revokeToken(request, {})
		.then((response: RevokeTokenResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postRequestPasswordReset = async (email: string): Promise<RequestPasswordResetResponse> => {
	const request: RequestPasswordResetRequest = new RequestPasswordResetRequest();
	request.setEmail(email);

	const client: ClaimsBFFControllerClient = getBffRequest();
	return client
		.requestPasswordReset(request, null)
		.then((response: RequestPasswordResetResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

export const postResetPassword = async (
	email: string,
	password: string,
	token: string
): Promise<ResetPasswordResponse> => {
	const request: ResetPasswordRequest = new ResetPasswordRequest();
	request.setEmail(email);
	request.setPassword(password);
	request.setToken(token);

	const client: ClaimsBFFControllerClient = getBffRequest();
	return client
		.resetPassword(request, null)
		.then((response: ResetPasswordResponse) => response)
		.catch((error) => {
			console.error(error);
			const err = parseRpcError(error.message);
			switch (err.errorCode) {
				case "token_expired":
					// eslint-disable-next-line no-throw-literal
					throw "Your password reset token has expired. Please request another password reset.";
				case "failed_password_requirements":
					// eslint-disable-next-line no-throw-literal
					throw "The password requirements were not met. Please try again with a valid password.";
				default:
					// eslint-disable-next-line no-throw-literal
					throw "Password reset failed. Please try again or else contact support.";
			}
		});
};

export const postResendWelcome = async (id: number): Promise<RequestPasswordResetResponse> => {
	const token: string = getApiToken();
	const request: ResendWelcomeRequest = new ResendWelcomeRequest();
	request.setUserId(id);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.resendWelcome(request, null)
		.then((response: ResendWelcomeResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

/**
 * Handles the AuthService::getUsers endpoint
 * @param params GetUsersRequest parameters
 * @returns GetUsersResponse object
 */
export const getUsers = async (params: {
	emailFilter?: string;
	nameFilter?: string;
	externalIdFilter?: string;
	rolesList?: string[];
	status?: string;
	userType?: string;
	sortBy?: string;
	sortOrder?: string;
	page?: number;
	pageSize?: number;
}): Promise<GetUsersResponse> => {
	const token: string = getApiToken();
	const request: GetUsersRequest = new GetUsersRequest();

	if (params.emailFilter) {
		request.setEmailFilter(params.emailFilter);
	}
	if (params.nameFilter) {
		request.setNameFilter(params.nameFilter);
	}
	if (params.externalIdFilter) {
		request.setExternalIdFilter(params.externalIdFilter);
	}
	if (params.rolesList && params.rolesList.length > 0) {
		params.rolesList.forEach((role) => request.addRoles(role));
	}
	if (params.status) {
		request.setStatus(params.status);
	}
	if (params.userType) {
		request.setUserType(params.userType);
	}
	if (params.sortBy) {
		request.setSortBy(params.sortBy);
	}
	if (params.sortOrder) {
		request.setSortOrder(params.sortOrder);
	}
	if (params.page) {
		request.setPage(params.page);
	}
	if (params.pageSize) {
		request.setPageSize(params.pageSize);
	}

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.getUsers(request, {})
		.then((response: GetUsersResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

/**
 * Handles the AuthService::createUser endpoint
 * @returns CreateUserResponse object
 */
export const createUser = async (
	email: string,
	name: string,
	username: string,
	roles: string,
	externalId?: string,
	password?: string
): Promise<CreateUserResponse> => {
	const token: string = getApiToken();
	const request: CreateUserRequest = new CreateUserRequest();

	request.setEmail(email);
	request.setName(name);
	request.setUsername(username);
	request.setRoles(roles);
	if (password) {
		request.setPassword(password);
	} else {
		// If no password given, create a random password to be reset during password reset
		request.setPassword(Math.random().toString(36).slice(-8));
	}
	if (externalId) {
		request.setExternalId(externalId);
	}

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.createUser(request, {})
		.then((response: CreateUserResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

/**
 * Handles the AuthService::getUserById endpoint
 * @param userId User ID
 * @returns GetUserByIdResponse object with basic User info
 */
export const getUserById = async (userId: number): Promise<GetUserByIdResponse> => {
	const token: string = getApiToken();
	const request: GetUserByIdRequest = new GetUserByIdRequest();
	request.setUserId(userId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.getUserById(request, {})
		.then((response: GetUserByIdResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

/**
 * Handles the AuthService::getUserDataById endpoint
 * @param userId User ID
 * @returns GetUserDataByIdResponse object with full UserData (includes status, timestamps)
 */
export const getUserDataById = async (userId: number): Promise<GetUserDataByIdResponse> => {
	const token: string = getApiToken();
	const request: GetUserDataByIdRequest = new GetUserDataByIdRequest();
	request.setUserId(userId);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.getUserDataById(request, {})
		.then((response: GetUserDataByIdResponse) => response)
		.catch((err) => {
			console.error(err);
			throw err;
		});
};

/**
 * Handles the ClaimsBFF:upsert_user endpoint
 * @param user UpsertUser object
 * @returns UpsertUserResponse
 */
export const upsertUser = async (user: KVP): Promise<UpsertUserResponse> => {
	const token: string = getApiToken();

	const request: UpsertUserRequest = new UpsertUserRequest();

	if (user.id) request.setId(+user.id);
	if (user.email) request.setEmail(user.email.toString());
	if (user.password) request.setPassword(user.password.toString());
	if (user.name) request.setName(user.name.toString());
	if (user.username) request.setUsername(user.username.toString());
	if (user.externalId) request.setExternalId(user.externalId.toString());

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.upsertUser(request, {})
		.then((response: UpsertUserResponse) => {
			return response;
		})
		.catch((err) => {
			console.error(err);
			throw parseRpcError(err.message).errorMessage;
		});
};
